/** @typedef {import('@tiptap/core').JSONContent} MessageNode */
/** Version marker applies only to text written by the rich editor. Legacy strings are literal. */
export const RICH_MESSAGE_PREFIX = '\u001eLDG:rich:1\n';

/** @param {unknown} value */
export function messageLinkHref(value) {
  if (typeof value === 'string' && /^www\./i.test(value)) value = `https://${value}`;
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value) || /[\u0000-\u0020]/.test(value)) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

/** @param {unknown} value @returns {MessageNode} */
function literalDocument(value) {
  const text = typeof value === 'string' ? value : '';
  return { type: 'doc', content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }] };
}

/** Validate the complete small document. Invalid input never partially rewrites legacy text.
 * @param {unknown} value @returns {MessageNode | null}
 */
function validatedDocument(value) {
  let remaining = 10000;
  /** @param {any} node @param {number} depth @param {string} parent @returns {MessageNode} */
  function read(node, depth, parent) {
    if (!node || typeof node !== 'object' || Array.isArray(node) || depth > 48 || --remaining < 0) throw new Error('invalid document');
    const type = node.type;
    const allowed = parent === '' ? ['doc'] : parent === 'doc' || parent === 'listItem'
      ? ['paragraph', 'bulletList', 'orderedList'] : parent === 'paragraph' ? ['text', 'hardBreak'] : ['listItem'];
    if (!allowed.includes(type)) throw new Error('invalid node');
    if (type === 'text') {
      if (typeof node.text !== 'string' || !node.text) throw new Error('invalid text');
      /** @type {NonNullable<MessageNode['marks']>} */
      const marks = [];
      for (const mark of Array.isArray(node.marks) ? node.marks : []) {
        if (['bold', 'italic', 'strike', 'underline'].includes(mark?.type) && !marks.some(item => item.type === mark.type)) marks.push({ type: mark.type });
        if (mark?.type === 'link') {
          const href = messageLinkHref(mark.attrs?.href);
          if (href && !marks.some(item => item.type === 'link')) marks.push({ type: 'link', attrs: { href } });
        }
      }
      return { type, text: node.text, ...(marks.length ? { marks } : {}) };
    }
    if (type === 'hardBreak') return { type };
    if (!Array.isArray(node.content) && node.content !== undefined) throw new Error('invalid content');
    const content = (node.content || []).map((/** @type {unknown} */ child) => read(child, depth + 1, type));
    if (type !== 'paragraph' && content.length === 0) throw new Error('empty structural node');
    const start = node.attrs?.start;
    return { type, ...(type === 'orderedList' ? { attrs: { start: Number.isSafeInteger(start) && start > 0 ? start : 1 } } : {}), content };
  }
  try { return read(value, 0, ''); } catch { return null; }
}

/**
 * What a reader shows when a marked document cannot be read at all.
 *
 * It is deliberately a sentence rather than an empty string: a message that
 * vanishes reads as "nobody wrote to me", and a person who can see that
 * SOMETHING was sent can ask about it.
 */
export const UNREADABLE_MESSAGE_TEXT = 'This message could not be displayed';

/**
 * Best effort words out of a document that parsed as JSON but is not a valid
 * message. Never trusted as structure: every recovered run is emitted as plain
 * text with no marks, so a forged node type cannot smuggle a link or a style
 * in through the salvage path.
 *
 * @param {unknown} value @returns {string}
 */
function salvagedText(value) {
  /** @type {string[]} */
  const runs = [];
  let budget = 10000;
  /** @param {unknown} node @param {number} depth */
  const walk = (node, depth) => {
    if (depth > 48 || --budget < 0 || !node || typeof node !== 'object') return;
    if (Array.isArray(node)) { for (const child of node) walk(child, depth + 1); return; }
    const record = /** @type {Record<string, unknown>} */ (node);
    if (typeof record.text === 'string' && record.text) runs.push(record.text);
    if (record.type === 'hardBreak' || record.type === 'paragraph') runs.push('\n');
    walk(record.content, depth + 1);
  };
  walk(value, 0);
  return runs.join('').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Best effort words out of a document whose JSON was CUT, which is what a
 * truncating write path leaves behind. Only complete `"text": "..."` pairs are
 * taken, each decoded by the JSON parser rather than by hand, so a half
 * written final run is dropped instead of guessed at. Recovered runs are plain
 * text with no marks, same as the salvage above.
 *
 * @param {string} fragment @returns {string}
 */
function salvagedTextFromFragment(fragment) {
  /** @type {string[]} */
  const runs = [];
  const pattern = /"text"\s*:\s*("(?:[^"\\]|\\.)*")/g;
  let budget = 10000;
  for (let match = pattern.exec(fragment); match && --budget > 0; match = pattern.exec(fragment)) {
    try {
      const value = JSON.parse(match[1]);
      if (typeof value === 'string') runs.push(value);
    } catch { /* an unterminated literal is not recoverable */ }
  }
  return runs.join('').trim();
}

/**
 * The stored string turned into something a person can read.
 *
 * A value with no marker is literal, byte for byte, which is what keeps every
 * message written before this feature unchanged. A MARKED value that will not
 * parse or will not validate must never fall back to the whole stored string:
 * that prints the marker and raw JSON on a parent's phone, which is exactly
 * the failure a truncating write path produced. So the fallback is, in order,
 * the words recovered from whatever did parse, and then an honest sentence.
 *
 * @param {unknown} value @returns {MessageNode}
 */
export function messageDocument(value) {
  if (typeof value !== 'string' || !value.startsWith(RICH_MESSAGE_PREFIX)) return literalDocument(value);
  const body = value.slice(RICH_MESSAGE_PREFIX.length);
  // A marked value whose body was never a document at all: no serializer of
  // ours can write this, so it is somebody's literal text that happens to
  // start with the marker. Keep every byte, exactly as before.
  if (!body.startsWith('{')) return literalDocument(value);
  let parsed;
  try { parsed = JSON.parse(body); }
  catch { return literalDocument(salvagedTextFromFragment(body) || UNREADABLE_MESSAGE_TEXT); }
  const document = validatedDocument(parsed);
  if (document) return document;
  return literalDocument(salvagedText(parsed) || UNREADABLE_MESSAGE_TEXT);
}

/** @param {MessageNode} node @returns {string} */
function documentText(node) {
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return '\n';
  const separator = ['doc', 'bulletList', 'orderedList', 'listItem'].includes(node.type || '') ? '\n' : '';
  return (node.content || []).map(documentText).join(separator);
}

/** @param {unknown} value @returns {string} */
export function plainMessageText(value) { return documentText(messageDocument(value)); }

/** The character count people see, never the serialized rich-document size.
 * @param {unknown} value @returns {number}
 */
export function messageCharacterCount(value) { return plainMessageText(value).length; }

/** True only for a valid, explicitly formatted document.
 * @param {unknown} value @returns {boolean}
 */
export function hasRichMessageFormatting(value) {
  if (typeof value !== 'string' || !value.startsWith(RICH_MESSAGE_PREFIX)) return false;
  try { return validatedDocument(JSON.parse(value.slice(RICH_MESSAGE_PREFIX.length))) !== null; }
  catch { return false; }
}

/** Keep ordinary messages plain; explicitly mark only a document needing rich formatting.
 * @param {unknown} value @returns {string}
 */
export function serializeMessageDocument(value) {
  const doc = validatedDocument(value);
  if (!doc) throw new Error('The message formatting could not be saved. Clear the formatting and try again.');
  const plain = documentText(doc);
  /** @param {MessageNode} node @returns {boolean} */
  const isRich = node => Boolean(node.marks?.length) || ['bulletList', 'orderedList'].includes(node.type || '') || Boolean(node.content?.some(isRich));
  return isRich(doc) || plain.startsWith(RICH_MESSAGE_PREFIX) ? RICH_MESSAGE_PREFIX + JSON.stringify(doc) : plain;
}

/** The most room a stored formatted document may take, whatever its visible length. */
export const MAX_STORED_MESSAGE_LENGTH = 250000;

/**
 * The stored room a document may take for a lane whose visible cap is `max`.
 * Marks and link addresses make a formatted document longer than its words
 * (a 30-item list is under three times its words), but never without bound:
 * tied to the lane's own cap so a short-capped lane can never store or serve
 * a quarter-megabyte document hiding behind a few visible words.
 * @param {number} max @returns {number}
 */
export function storedMessageCeiling(max) {
  return Math.min(MAX_STORED_MESSAGE_LENGTH, Math.max(0, max) * 25);
}

/**
 * Bound a stored message by the words a person reads, never by its marks.
 * Plain text is trimmed and cut at `max` exactly as before. A formatted
 * document is kept whole when its words fit; one that does not fit falls back
 * to its words cut at `max`, so the words survive and a half-cut document is
 * never stored or shown.
 * @param {unknown} value @param {number} max @returns {string}
 */
export function boundedMessageText(value, max) {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!hasRichMessageFormatting(text)) return text.slice(0, max);
  const words = plainMessageText(text);
  return text.length <= storedMessageCeiling(max) && words.length <= max ? text : words.trim().slice(0, max);
}
